<script lang="ts" setup>
import { BasicButton, Picture } from '~/types';
import Container from '~/components/Container.vue';
import EmbedHtml from '~/components/Common/EmbedHtml.vue';
import ExternalLinkWarning from '~/components/ui/ExternalLinkWarning.vue';
import Image from '~/components/ui/Image.vue';
import YoutubeVideoEmbed from '~/components/Video/YoutubeVideoEmbed.vue';

interface Props {
  anchor?: string;
  title: string;
  description?: string;
  image?: Picture;
  buttons?: BasicButton[];
  youtube_url?: string;
}

defineProps<Props>();

const videoOptions = {
  autoplay: true,
  loop: {
    active: true,
  },
  muted: true,
  hideControls: true,
};
</script>

<template>
  <section :id="anchor" class="hero-w-video">
    <div v-if="youtube_url" class="hero-w-video__media-wrapper">
      <YoutubeVideoEmbed :url="youtube_url" :video-options="videoOptions" />
    </div>
    <div v-else-if="image" class="hero-w-video__media-wrapper">
      <Image v-if="image" :image="image" class="hero-w-video__image" />
    </div>
    <Container class="hero-w-video__container">
      <h2 v-if="title" class="hero-w-video__title">
        {{ title }}
      </h2>
      <EmbedHtml v-if="description" :content="description" class="hero-w-video__description" />
      <div v-if="buttons && buttons.length > 0" class="hero-w-video__buttons">
        <a
          v-for="(button, index) in buttons"
          :key="`hero-w-video-button-${index}`"
          :href="button.url"
          :target="button.target || '_blank'"
          class="hero-w-video__button"
        >
          {{ button.label }}
          <ExternalLinkWarning :target="button.target" />
        </a>
      </div>
    </Container>
  </section>
</template>

<style lang="scss" scoped>
.hero-w-video {
  @apply overflow-hidden flex flex-col relative mb-16 mx-auto;
  @apply xl:aspect-[16/9] xl:max-w-full xl:bg-dark xl:justify-end;
  @apply min-[1921px]:max-w-[1920px];

  &__media-wrapper {
    @apply before:hidden before:absolute before:w-full before:h-full before:inset-0 before:z-[1] before:bg-black/40;
    @apply xl:absolute xl:inset-0 xl:w-full xl:h-full;
    @apply xl:before:block;
  }

  &__image {
    @apply w-full h-auto;
    @apply xl:absolute xl:object-cover xl:object-center xl:inset-0 xl:h-full;
  }

  &__container {
    @apply pt-8 flex flex-col gap-6;
    @apply xl:relative xl:z-[2] xl:py-20;
    @apply 2xl:py-32;
  }

  &__title {
    @apply font-title text-[40px] leading-[1.1];
    @apply lg:text-[50px];
    @apply xl:text-[70px] xl:text-white xl:max-w-[60%];
    @apply 3xl:text-[90px];
  }

  &__description {
    @apply xl:max-w-[60%];
  }

  &__buttons {
    @apply flex items-center flex-wrap gap-6;
  }

  &__button {
    @apply bg-accent px-6 py-4 rounded-full text-white text-[16px] font-bold duration-300 hover:bg-white hover:text-accent;
    @apply 2xl:text-[20px];
  }

  :deep(.text-container > *) {
    @apply text-base-color;
    @apply xl:text-[24px] xl:text-white;
  }
}
</style>
